import { useEffect, useState } from 'react'

/**
 * The sub-classes defined in Settings → Takım Düzeni, for the roster builder.
 *
 * A sub-class has no key of its own (it is `{name, emoji}` on its position),
 * so the roster refers to it as `spec:<position>/<name>` — see `specKey`, and
 * `spec_key` in backend/matches/local.py, which must produce the same text.
 * The id is only ever looked up, never parsed: a position name may itself
 * contain a slash ("Supply/Air Transport").
 */
export interface MatchSpec {
  id: string
  name: string
  emoji: string
  className: string
}

export interface MatchSpecGroup {
  className: string
  classEmoji: string
  specs: MatchSpec[]
}

export interface MatchSpecs {
  groups: MatchSpecGroup[]
  byId: Record<string, MatchSpec>
}

export const specKey = (className: string, name: string) => `spec:${className}/${name}`

const EMPTY: MatchSpecs = { groups: [], byId: {} }

let cache: Promise<MatchSpecs> | null = null

interface ClassRow { name: string; emoji: string; specs?: { name: string; emoji: string }[] }

function load(): Promise<MatchSpecs> {
  cache ??= fetch('/api/match-classes', { credentials: 'include' })
    .then(r => (r.ok ? r.json() : []))
    .then((rows: ClassRow[]) => {
      const groups = rows
        .filter(c => c.specs?.length)
        .map(c => ({
          className: c.name,
          classEmoji: c.emoji,
          specs: c.specs!.map(s => ({ id: specKey(c.name, s.name), name: s.name, emoji: s.emoji, className: c.name })),
        }))
      const byId = Object.fromEntries(groups.flatMap(g => g.specs).map(s => [s.id, s]))
      return { groups, byId }
    })
    .catch(() => { cache = null; return EMPTY })
  return cache
}

/** Settings changed the list: the next reader fetches it again. */
export function invalidateMatchSpecs() { cache = null }

export function useMatchSpecs(): MatchSpecs {
  const [specs, setSpecs] = useState<MatchSpecs>(EMPTY)
  useEffect(() => {
    let alive = true
    load().then(s => { if (alive) setSpecs(s) })
    return () => { alive = false }
  }, [])
  return specs
}
